/* eslint-disable @typescript-eslint/no-explicit-any */
import {useQuery, UseQueryOptions, useQueryClient} from '@tanstack/react-query';
import {categoryApiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {B2CCategoryResponse, OrgCategoryResponse} from '#types/category';
import {
  getBenefitCategoriesResponseTransformer,
  getOrganisationB2CCategoriesResponseTransformer
} from '#utils/response-transformers/categories';
import {STALE_TIME, GC_TIME, PREFETCH_TIME} from '#constants';

// Hook for B2C Categories
export function useB2CCategories(
  options?: Omit<UseQueryOptions<B2CCategoryResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.categories.b2c(),
    queryFn: async () => {
      const response = (await categoryApiClient.getB2CCategories()) as any;
      let categories: any[] = [];

      if (
        response?.data?.categories &&
        Array.isArray(response.data.categories)
      ) {
        categories = response.data.categories;
      } else if (response?.categories && Array.isArray(response.categories)) {
        categories = response.categories;
      } else if (response?.data && Array.isArray(response.data)) {
        categories = response.data;
      } else if (Array.isArray(response)) {
        categories = response;
      }

      return {
        categories: Array.isArray(categories) ? categories : []
      } as B2CCategoryResponse;
    },
    staleTime: STALE_TIME.CATEGORIES, // data stays not stale for staleTime
    gcTime: GC_TIME.CATEGORIES, // cache is kept in memory for gcTime, if unused by then - clean
    refetchOnMount: false, // DO NOT refetch if data is already in cache and not stale
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options
  });
}

// Hook for Organisation Categories
export function useOrgCategories(
  params?: {search?: string},
  options?: Omit<UseQueryOptions<OrgCategoryResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.categories.organisation(), params],
    queryFn: () =>
      categoryApiClient.getOrgCategories(
        params
      ) as Promise<OrgCategoryResponse>,
    staleTime: STALE_TIME.CATEGORIES_ORG,
    gcTime: GC_TIME.CATEGORIES_ORG,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: true,
    ...options
  });
}

// Hook for Organisation B2C Categories
export function useOrganisationB2CCategories(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.categories.organisationB2c(),
    queryFn: async () => {
      const response = await categoryApiClient.getOrganisationB2CCategories();
      return getOrganisationB2CCategoriesResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.CATEGORIES_ORG,
    gcTime: GC_TIME.CATEGORIES_ORG,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options
  });
}

// Hook for Benefit Categories
export function useBenefitCategories(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.categories.b2c(), // Use same query key as B2C categories since it's the same endpoint
    queryFn: async () => {
      const response = await categoryApiClient.getBenefitCategories();
      return getBenefitCategoriesResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.CATEGORIES,
    gcTime: GC_TIME.CATEGORIES,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options
  });
}

// Utility hook to prefetch categories
export function usePrefetchCategories() {
  const queryClient = useQueryClient();

  return {
    prefetchB2CCategories: () => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.categories.b2c(),
        queryFn: () => categoryApiClient.getB2CCategories(),
        staleTime: PREFETCH_TIME.CATEGORIES
      });
    },

    prefetchOrgCategories: (params?: {search?: string}) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.categories.organisation(),
        queryFn: () => categoryApiClient.getOrgCategories(params),
        staleTime: STALE_TIME.CATEGORIES_ORG
      });
    },

    prefetchAllCategories: () => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.categories.b2c(),
        queryFn: () => categoryApiClient.getB2CCategories(),
        staleTime: PREFETCH_TIME.CATEGORIES
      });

      queryClient.prefetchQuery({
        queryKey: queryKeys.categories.organisation(),
        queryFn: () => categoryApiClient.getOrgCategories(),
        staleTime: STALE_TIME.CATEGORIES_ORG
      });
    }
  };
}

// Utility hook to invalidate categories cache
export function useInvalidateCategories() {
  const queryClient = useQueryClient();

  return {
    invalidateB2CCategories: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.categories.b2c()});
    },

    invalidateOrgCategories: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.categories.organisation()
      });
    },

    invalidateAllCategories: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.categories.all});
    }
  };
}
